/**
 * SEO Utility Functions
 * Centralized helpers for meta tags, structured data, and canonical URLs
 */

// Base URL for the site (update in production)
export const SITE_URL = 'https://thetabber.com';
export const SITE_NAME = 'TheTabber';

/**
 * Generate canonical URL for a given path
 */
export function generateCanonicalUrl(path: string): string {
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${SITE_URL}${cleanPath}`;
}

/**
 * Generate Organization JSON-LD structured data
 */
export function generateOrganizationJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/logo.png`,
    sameAs: [],
    description:
      'Multi-platform social media publishing tool. Publish to TikTok, Instagram, YouTube, Facebook, X, LinkedIn, Pinterest, Threads, and Bluesky.',
  };
}

/**
 * Generate WebSite JSON-LD structured data
 */
export function generateWebSiteJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    description:
      'Publish your content to 9+ social media platforms simultaneously. Create stunning UGC videos, add AI captions, and grow your audience everywhere.',
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
    },
  };
}

/**
 * Generate SoftwareApplication JSON-LD for platform scheduler pages
 */
export function generateSoftwareApplicationJsonLd(platform: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: `${SITE_NAME} ${platform} Scheduler`,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
    },
    description: `Schedule and automate your ${platform} posts with ${SITE_NAME}. Save time with multi-platform publishing.`,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      ratingCount: '150',
    },
  };
}

/**
 * Generate FAQ JSON-LD structured data
 */
export function generateFaqJsonLd(faqs: Array<{ question: string; answer: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

/**
 * Generate BreadcrumbList JSON-LD structured data
 */
export function generateBreadcrumbJsonLd(items: Array<{ name: string; url: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

/**
 * Platform data for scheduler pages
 */
export interface PlatformData {
  slug: string;
  name: string;
  displayName: string;
  color: string;
  description: string;
  longDescription: string;
  metaDescription: string;
  keywords: string;
  features: string[];
  howItWorks: Array<{ step: number; title: string; description: string }>;
  useCases: Array<{ title: string; description: string }>;
  stats: { users: string; engagement: string; growth: string };
  faqs: Array<{ question: string; answer: string }>;
}

export const platformsData: PlatformData[] = [
  {
    slug: 'x-twitter',
    name: 'X (Twitter)',
    displayName: 'X (Twitter)',
    color: '#000000',
    description:
      'Post and schedule tweets, threads, and media to X (formerly Twitter) with AI-powered text generation. One-click publishing to multiple platforms.',
    longDescription:
      "TheTabber is the ultimate X (Twitter) scheduler for creators, marketers, and businesses. Create engaging tweets with AI assistance, schedule threads in advance, and post to X and 8 other platforms simultaneously. Save hours every week with our intuitive dashboard, smart scheduling, and powerful automation features. Whether you're building a personal brand or managing multiple business accounts, TheTabber helps you maintain a consistent X presence without the daily grind.",
    metaDescription:
      'Free X (Twitter) scheduler and posting tool. Schedule tweets, threads & media. AI-powered captions. Post to X + 8 other platforms in one click. Start free today.',
    keywords:
      'x scheduler, twitter scheduler, schedule tweets, twitter scheduling tool, tweet scheduler free, twitter post scheduler, schedule twitter posts, x posting tool, twitter automation, social media scheduler',
    features: [
      'Post instantly or schedule for later',
      'AI-powered text and caption generation',
      'Create UGC videos and grid content',
      'Multi-account management',
      'One-click cross-posting to 9+ platforms',
      'Thread scheduling and management',
      'Image and video post support',
      'Calendar view for content planning',
    ],
    howItWorks: [
      {
        step: 1,
        title: 'Connect Your X Account',
        description:
          'Securely link your X account to TheTabber with OAuth. Takes less than 30 seconds.',
      },
      {
        step: 2,
        title: 'Create Your Content',
        description:
          'Write your tweet or let AI generate engaging content. Add images, videos, or create threads.',
      },
      {
        step: 3,
        title: 'Schedule or Post',
        description:
          'Post instantly with one click or schedule for the perfect time. Add to multiple platforms if you want.',
      },
    ],
    useCases: [
      {
        title: 'Content Creators',
        description:
          "Schedule a week's worth of tweets in one sitting. Maintain consistency without being online 24/7.",
      },
      {
        title: 'Marketing Teams',
        description:
          'Coordinate campaigns across X and other platforms. Approve content and schedule launches.',
      },
      {
        title: 'Small Businesses',
        description:
          'Stay active on X without hiring a social media manager. Post promotions at optimal times.',
      },
      {
        title: 'Personal Brands',
        description:
          'Build thought leadership with consistent posting. Let AI help craft your message.',
      },
    ],
    stats: {
      users: '600M+ monthly users',
      engagement: '23% higher with scheduled posts',
      growth: 'Save 10+ hours/week',
    },
    faqs: [
      {
        question: 'Can I post to X instantly or do I have to schedule?',
        answer:
          'Both! TheTabber lets you post immediately with one click or schedule for any future time. You choose what works best for your workflow.',
      },
      {
        question: 'How does the AI content generation work?',
        answer:
          "TheTabber's AI assistant helps generate engaging captions, suggests hashtags, and can even create UGC-style videos and images for your posts.",
      },
      {
        question: 'Can I post to X and other platforms at the same time?',
        answer:
          "Yes! With TheTabber's one-click publishing, you can post or schedule the same content to X and 8 other platforms simultaneously.",
      },
      {
        question: "Is TheTabber's X scheduler free?",
        answer:
          'TheTabber offers a free tier that lets you connect accounts and post to X. Premium plans unlock unlimited scheduling and advanced features.',
      },
      {
        question: 'Can I schedule X threads with TheTabber?',
        answer:
          'Yes! Create multi-tweet threads and schedule them to post at your chosen time. Perfect for storytelling and longer content.',
      },
      {
        question: 'How do I connect my X account to TheTabber?',
        answer:
          "Click 'Connect Account', sign in to X when prompted, and authorize TheTabber. It takes less than 30 seconds and uses secure OAuth authentication.",
      },
    ],
  },
  {
    slug: 'instagram',
    name: 'Instagram',
    displayName: 'Instagram',
    color: '#E4405F',
    description:
      'Post and schedule Instagram content with AI-generated captions. Create stunning reels and images, then publish to multiple platforms instantly.',
    longDescription:
      "TheTabber is the most powerful Instagram scheduler for creators and businesses. Schedule Reels, feed posts, and carousels in advance. Our AI generates engaging captions and trending hashtags automatically. Post to Instagram and 8 other platforms with one click. Whether you're an influencer, brand, or small business, TheTabber helps you maintain a consistent Instagram presence and grow your following without spending hours every day on content.",
    metaDescription:
      'Free Instagram scheduler - schedule Reels, posts & carousels. AI captions & hashtags. Post to Instagram + 8 platforms in one click. Try free today.',
    keywords:
      'instagram scheduler, schedule instagram posts, instagram reels scheduler, instagram post scheduler free, schedule reels, instagram scheduling tool, instagram automation, social media scheduler, instagram planner',
    features: [
      'Post instantly or schedule reels and posts',
      'AI-generated captions and hashtags',
      'UGC video creator with templates',
      'Visual content calendar',
      'One-click cross-posting to 9+ platforms',
      'Carousel post support',
      'Best time to post recommendations',
      'Multi-account management',
    ],
    howItWorks: [
      {
        step: 1,
        title: 'Connect Instagram',
        description:
          'Link your Instagram Business or Creator account securely. Works with personal and brand accounts.',
      },
      {
        step: 2,
        title: 'Upload Your Content',
        description:
          'Upload Reels, images, or carousels. AI generates captions and suggests trending hashtags.',
      },
      {
        step: 3,
        title: 'Schedule & Publish',
        description:
          'Post now or schedule for peak engagement times. Cross-post to TikTok, YouTube and more.',
      },
    ],
    useCases: [
      {
        title: 'Influencers',
        description:
          'Batch create a week of Reels and posts. Schedule for optimal times without being glued to your phone.',
      },
      {
        title: 'E-commerce Brands',
        description:
          'Schedule product launches and promotions. Coordinate Instagram with other platforms.',
      },
      {
        title: 'Content Agencies',
        description:
          'Manage multiple client accounts from one dashboard. Streamline approval workflows.',
      },
      {
        title: 'Local Businesses',
        description:
          'Stay active on Instagram without a dedicated social media hire. Post consistently and grow.',
      },
    ],
    stats: {
      users: '2B+ monthly users',
      engagement: '29% higher with optimal timing',
      growth: 'Save 10+ hours/week',
    },
    faqs: [
      {
        question: 'Can I create Instagram Reels with TheTabber?',
        answer:
          'Yes! TheTabber includes a UGC video creator and grid video maker. Create professional reels with AI captions, then post or schedule them.',
      },
      {
        question: 'Does TheTabber help with Instagram captions?',
        answer:
          'Absolutely! Our AI assistant generates engaging captions, suggests relevant hashtags, and helps optimize your content for maximum reach.',
      },
      {
        question: 'Can I post to Instagram and TikTok at the same time?',
        answer:
          "Yes! TheTabber's one-click publishing lets you post the same content to Instagram, TikTok, and 7 other platforms simultaneously.",
      },
      {
        question: "Is TheTabber's Instagram scheduler free?",
        answer:
          'Yes! TheTabber offers a free tier for Instagram scheduling. Premium plans unlock unlimited posts and advanced features.',
      },
      {
        question: 'Can I schedule Instagram carousels?',
        answer:
          'Yes! Upload multiple images and TheTabber will post them as a carousel to your Instagram feed at your scheduled time.',
      },
      {
        question: 'Do I need a Business account for Instagram scheduling?',
        answer:
          "Yes, Instagram requires a Business or Creator account for third-party scheduling. It's free to switch in Instagram settings.",
      },
    ],
  },
  {
    slug: 'linkedin',
    name: 'LinkedIn',
    displayName: 'LinkedIn',
    color: '#0A66C2',
    description:
      'Post and schedule professional LinkedIn content with AI assistance. Generate engaging posts and publish to multiple platforms in one click.',
    longDescription:
      "TheTabber is the best LinkedIn scheduler for professionals and B2B marketers. Create thought leadership content with AI assistance, schedule posts for peak business hours, and manage both personal profiles and Company Pages from one dashboard. Our AI understands LinkedIn's professional tone and helps you craft posts that drive engagement. Perfect for consultants, executives, recruiters, and marketing teams who want to build their LinkedIn presence efficiently.",
    metaDescription:
      'Free LinkedIn scheduler - schedule posts to profiles & Company Pages. AI-powered professional content. Post to LinkedIn + 8 platforms. Try free.',
    keywords:
      'linkedin scheduler, schedule linkedin posts, linkedin post scheduler, linkedin scheduling tool free, linkedin automation, linkedin content scheduler, company page scheduler, linkedin planner, b2b social media scheduler',
    features: [
      'Post instantly or schedule content',
      'AI-powered professional content generation',
      'Personal and company page support',
      'Multi-account management',
      'One-click cross-posting to 9+ platforms',
      'Document and carousel posts',
      'Video post support',
      'Best B2B posting times',
    ],
    howItWorks: [
      {
        step: 1,
        title: 'Connect LinkedIn',
        description:
          'Link your personal profile or Company Page. Secure OAuth - we never see your password.',
      },
      {
        step: 2,
        title: 'Create Professional Content',
        description:
          'Write your post or let AI generate thought leadership content. Add images, documents, or videos.',
      },
      {
        step: 3,
        title: 'Schedule for Business Hours',
        description:
          'Post now or schedule for when professionals are most active. Tuesday-Thursday mornings are peak times.',
      },
    ],
    useCases: [
      {
        title: 'Thought Leaders',
        description:
          'Build your personal brand with consistent, valuable content. Let AI help craft your insights.',
      },
      {
        title: 'B2B Marketers',
        description:
          'Schedule campaigns across LinkedIn and other platforms. Coordinate with sales team activities.',
      },
      {
        title: 'Recruiters',
        description:
          'Stay active and visible to candidates. Share job posts and company culture content consistently.',
      },
      {
        title: 'Consultants',
        description:
          'Establish expertise in your field. Schedule educational content that attracts clients.',
      },
    ],
    stats: {
      users: '1B+ members',
      engagement: 'Professional content peaks Tue-Thu',
      growth: 'Save 5+ hours/week',
    },
    faqs: [
      {
        question: 'Can I post to LinkedIn instantly?',
        answer:
          'Yes! Post immediately or schedule for later. TheTabber gives you full control over when your content goes live.',
      },
      {
        question: 'Does the AI help with LinkedIn content?',
        answer:
          "Absolutely! Our AI generates professional, engaging content optimized for LinkedIn's audience and format.",
      },
      {
        question: 'Can I manage multiple LinkedIn accounts?',
        answer:
          'Yes! Connect multiple personal profiles and company pages, and manage them all from one dashboard.',
      },
      {
        question: "Is TheTabber's LinkedIn scheduler free?",
        answer:
          'Yes! TheTabber offers a free tier for LinkedIn scheduling. Premium plans unlock unlimited posts and team features.',
      },
      {
        question: 'Can I schedule to Company Pages?',
        answer:
          'Yes! Connect your LinkedIn Company Page and schedule posts directly to it. You need admin access to the page.',
      },
      {
        question: "What's the best time to post on LinkedIn?",
        answer:
          'Tuesday through Thursday mornings (8-11 AM) typically see the highest engagement. TheTabber helps you schedule at optimal times.',
      },
    ],
  },
  {
    slug: 'facebook',
    name: 'Facebook',
    displayName: 'Facebook',
    color: '#1877F2',
    description:
      'Post and schedule Facebook content with AI-powered text and video generation. Publish to pages and profiles across multiple platforms instantly.',
    longDescription:
      'TheTabber is the complete Facebook scheduler for businesses and creators. Schedule posts, Reels, and videos to your Facebook Pages. Our AI generates engaging captions that drive comments and shares. Manage multiple Pages from one dashboard and cross-post to Instagram, TikTok, and 6 other platforms with one click. Perfect for businesses, agencies, and creators who want to maintain an active Facebook presence without the daily time investment.',
    metaDescription:
      'Free Facebook scheduler - schedule posts, Reels & videos to Pages. AI captions. Post to Facebook + 8 platforms in one click. Start free today.',
    keywords:
      'facebook scheduler, schedule facebook posts, facebook post scheduler, facebook page scheduler, facebook scheduling tool free, facebook automation, social media scheduler, facebook planner, schedule facebook reels',
    features: [
      'Post instantly or schedule to pages and profiles',
      'AI-generated captions and content',
      'UGC video and image creation',
      'Multi-page management',
      'One-click cross-posting to 9+ platforms',
      'Facebook Reels scheduling',
      'Video post support',
      'Link preview posts',
    ],
    howItWorks: [
      {
        step: 1,
        title: 'Connect Your Facebook Pages',
        description:
          'Link your Facebook Pages securely. You need admin access to the Pages you want to manage.',
      },
      {
        step: 2,
        title: 'Create Engaging Content',
        description:
          'Upload videos, images, or let AI help write compelling captions that drive engagement.',
      },
      {
        step: 3,
        title: 'Schedule & Cross-Post',
        description:
          'Post now or schedule for optimal times. Cross-post to Instagram and other platforms.',
      },
    ],
    useCases: [
      {
        title: 'Local Businesses',
        description:
          'Stay active on Facebook to reach local customers. Schedule promotions and updates in advance.',
      },
      {
        title: 'E-commerce Stores',
        description:
          'Schedule product posts and sales announcements. Cross-post to Instagram Shopping.',
      },
      {
        title: 'Marketing Agencies',
        description:
          'Manage multiple client Pages from one dashboard. Coordinate campaigns across platforms.',
      },
      {
        title: 'Content Creators',
        description:
          'Cross-post your content to Facebook alongside Instagram, TikTok, and YouTube.',
      },
    ],
    stats: {
      users: '3B+ monthly users',
      engagement: 'Video gets 59% more engagement',
      growth: 'Save 10+ hours/week',
    },
    faqs: [
      {
        question: 'Can I post to Facebook right away?',
        answer:
          'Yes! Post immediately with one click or schedule for the perfect time. TheTabber makes both equally easy.',
      },
      {
        question: 'Does TheTabber create videos for Facebook?',
        answer:
          'Yes! Use our UGC video creator and grid video maker to create engaging content, then post or schedule it instantly.',
      },
      {
        question: 'Can I post to Facebook and Instagram together?',
        answer:
          "Absolutely! TheTabber's multi-platform publishing lets you post to Facebook, Instagram, and 7 other platforms simultaneously.",
      },
      {
        question: "Is TheTabber's Facebook scheduler free?",
        answer:
          'Yes! TheTabber offers a free tier for Facebook scheduling. Premium plans unlock unlimited scheduling and advanced features.',
      },
      {
        question: 'Can I schedule Facebook Reels?',
        answer:
          'Yes! Upload vertical videos and schedule them as Facebook Reels. Perfect for cross-posting your TikTok and Instagram Reels content.',
      },
      {
        question: 'Can I schedule to multiple Facebook Pages?',
        answer:
          'Yes! Connect all your Facebook Pages and schedule content to multiple Pages at once. Perfect for agencies and multi-location businesses.',
      },
    ],
  },
  {
    slug: 'tiktok',
    name: 'TikTok',
    displayName: 'TikTok',
    color: '#000000',
    description:
      'Post and schedule TikTok videos with AI-generated captions. Create viral content and publish across multiple platforms in one click.',
    longDescription:
      "TheTabber is the ultimate TikTok scheduler for creators who want to go viral. Upload your videos, let AI generate engaging captions with trending hashtags, and schedule posts for peak viral times. Cross-post to Instagram Reels, YouTube Shorts, and 6 other platforms with one click. Whether you're a content creator, brand, or influencer, TheTabber helps you maintain a consistent TikTok presence and maximize your chances of going viral.",
    metaDescription:
      'Free TikTok scheduler - schedule videos with AI captions. Post to TikTok + Instagram Reels + YouTube Shorts in one click. Start free today.',
    keywords:
      'tiktok scheduler, schedule tiktok videos, tiktok post scheduler, tiktok scheduling tool free, tiktok automation, schedule tiktok posts, tiktok video scheduler, tiktok planner, social media scheduler',
    features: [
      'Post instantly or schedule videos',
      'AI-powered auto-captions',
      'UGC video creator with templates',
      'Multi-account support',
      'One-click cross-posting to 9+ platforms',
      'Privacy and comment controls',
      'Duet and stitch settings',
      'Video trimming and editing',
    ],
    howItWorks: [
      {
        step: 1,
        title: 'Connect TikTok',
        description:
          'Link your TikTok account securely. Works with personal and business accounts.',
      },
      {
        step: 2,
        title: 'Upload Your Video',
        description:
          'Upload your video and let AI generate captions. Trim, add text, and customize.',
      },
      {
        step: 3,
        title: 'Schedule for Viral Times',
        description:
          'Post now or schedule for when TikTok engagement peaks. Cross-post to Reels and Shorts.',
      },
    ],
    useCases: [
      {
        title: 'TikTok Creators',
        description:
          'Batch create content and schedule a week at a time. Focus on creating, not posting.',
      },
      {
        title: 'Brands & Products',
        description: 'Schedule product demos and promotions. Cross-post to Instagram and YouTube.',
      },
      {
        title: 'Influencers',
        description:
          'Maintain multiple TikTok accounts. Schedule content for optimal engagement times.',
      },
      {
        title: 'Small Businesses',
        description: 'Join TikTok without the daily time commitment. Schedule and forget.',
      },
    ],
    stats: {
      users: '1.5B+ monthly users',
      engagement: 'Evening posts see highest views',
      growth: 'Save 10+ hours/week',
    },
    faqs: [
      {
        question: 'How do I post to TikTok with TheTabber?',
        answer:
          "Connect your account, upload or create your video, let AI generate captions, then post instantly or schedule. It's that simple!",
      },
      {
        question: 'Can I add captions to my TikTok videos?',
        answer:
          "Yes! TheTabber's AI automatically transcribes and adds beautiful captions to your videos. Edit timing and styles as needed.",
      },
      {
        question: 'Can I post to TikTok and YouTube Shorts at once?',
        answer:
          'Absolutely! Use TheTabber to post the same video to TikTok, YouTube Shorts, Instagram Reels, and more with one click.',
      },
      {
        question: "Is TheTabber's TikTok scheduler free?",
        answer:
          'Yes! TheTabber offers a free tier for TikTok scheduling. Premium plans unlock unlimited scheduling and AI features.',
      },
      {
        question: 'Can I control who sees my scheduled TikTok posts?',
        answer:
          'Yes! Set privacy levels (Public, Friends, Private), enable/disable comments, duets, and stitches for each scheduled post.',
      },
      {
        question: "What's the best time to post on TikTok?",
        answer:
          'Evening hours (7-11 PM) typically see the highest engagement. TheTabber helps you schedule at optimal times for virality.',
      },
    ],
  },
  {
    slug: 'youtube',
    name: 'YouTube',
    displayName: 'YouTube',
    color: '#FF0000',
    description:
      'Post and schedule YouTube videos and Shorts with AI assistance. Create content and publish across multiple platforms instantly.',
    longDescription:
      'TheTabber is the complete YouTube scheduler for creators and businesses. Schedule long-form videos and YouTube Shorts in advance. Our AI generates SEO-optimized titles, descriptions, and tags to help your videos get discovered. Cross-post Shorts to TikTok, Instagram Reels, and 6 other platforms with one click. Perfect for YouTubers who want to maintain a consistent upload schedule and grow their channel without the daily grind.',
    metaDescription:
      'Free YouTube scheduler - schedule videos & Shorts. AI titles & descriptions. Post to YouTube + TikTok + Instagram in one click. Start free.',
    keywords:
      'youtube scheduler, schedule youtube videos, youtube shorts scheduler, youtube video scheduler, youtube scheduling tool free, youtube automation, schedule youtube uploads, youtube planner, social media scheduler',
    features: [
      'Post instantly or schedule videos and Shorts',
      'AI-generated titles and descriptions',
      'Multi-channel support',
      'Thumbnail and metadata management',
      'One-click cross-posting to 9+ platforms',
      'Visibility settings (Public/Unlisted/Private)',
      'Category selection',
      'Made for kids compliance',
    ],
    howItWorks: [
      {
        step: 1,
        title: 'Connect YouTube',
        description:
          'Link your YouTube channel with Google OAuth. Connect multiple channels if you have them.',
      },
      {
        step: 2,
        title: 'Upload Your Video',
        description: 'Upload your video and let AI generate SEO-optimized titles and descriptions.',
      },
      {
        step: 3,
        title: 'Schedule Your Upload',
        description:
          'Set visibility, category, and schedule time. Cross-post Shorts to TikTok and Reels.',
      },
    ],
    useCases: [
      {
        title: 'YouTubers',
        description:
          'Maintain a consistent upload schedule. Batch upload and schedule a month of content.',
      },
      {
        title: 'Shorts Creators',
        description:
          'Cross-post TikToks and Reels to YouTube Shorts automatically. Grow on multiple platforms.',
      },
      {
        title: 'Businesses',
        description:
          'Schedule product videos and tutorials. Build a YouTube presence for your brand.',
      },
      {
        title: 'Educators',
        description:
          'Schedule course content and tutorials. Let AI help with descriptions and tags.',
      },
    ],
    stats: {
      users: '2.5B+ monthly users',
      engagement: 'Shorts get 6x more reach',
      growth: 'Save 10+ hours/week',
    },
    faqs: [
      {
        question: 'Can I post YouTube Shorts with TheTabber?',
        answer:
          'Yes! Create and post YouTube Shorts instantly, or schedule them for later. Perfect for repurposing TikTok and Reels content.',
      },
      {
        question: 'Does TheTabber help write video descriptions?',
        answer:
          'Yes! Our AI generates optimized titles, descriptions, and tags to help your videos get discovered.',
      },
      {
        question: 'Can I post to YouTube and other platforms together?',
        answer:
          'Absolutely! TheTabber lets you post the same video to YouTube, TikTok, Instagram, and 6 other platforms at once.',
      },
      {
        question: "Is TheTabber's YouTube scheduler free?",
        answer:
          'Yes! TheTabber offers a free tier for YouTube scheduling. Premium plans unlock unlimited uploads and AI features.',
      },
      {
        question: 'Can I schedule to multiple YouTube channels?',
        answer:
          'Yes! Connect all your YouTube channels and schedule content to multiple channels at once.',
      },
      {
        question: 'Can I set video visibility when scheduling?',
        answer:
          'Yes! Choose Public, Unlisted, or Private for each scheduled video. Change visibility settings anytime before publishing.',
      },
    ],
  },
  {
    slug: 'bluesky',
    name: 'Bluesky',
    displayName: 'Bluesky',
    color: '#0085FF',
    description:
      'Post and schedule to Bluesky with AI-powered content generation. Grow your presence on the decentralized network with multi-platform publishing.',
    longDescription:
      "TheTabber is the best Bluesky scheduler for creators who want to build presence on the decentralized social web. Schedule posts with text and images, let AI help craft engaging content, and cross-post to X, Threads, and 6 other platforms with one click. Whether you're migrating from Twitter or building a fresh presence on Bluesky, TheTabber helps you maintain consistency without the daily time investment. Perfect for early adopters and creators who want to be where social media is heading.",
    metaDescription:
      'Free Bluesky scheduler - schedule posts & images. AI-powered captions. Post to Bluesky + X + Threads in one click. Start free today.',
    keywords:
      'bluesky scheduler, schedule bluesky posts, bluesky post scheduler, bluesky scheduling tool, bluesky automation, schedule posts bluesky, bluesky planner, decentralized social media scheduler, at protocol scheduler',
    features: [
      'Post instantly or schedule posts',
      'AI-powered text generation',
      'Image and text post support',
      'Multi-account management',
      'One-click cross-posting to 9+ platforms',
      'Up to 4 images per post',
      'Link card support',
      'App password authentication',
    ],
    howItWorks: [
      {
        step: 1,
        title: 'Connect Bluesky',
        description:
          'Enter your Bluesky handle and app password. Generate an app password in Bluesky settings for security.',
      },
      {
        step: 2,
        title: 'Create Your Post',
        description: 'Write your post or let AI generate engaging content. Add up to 4 images.',
      },
      {
        step: 3,
        title: 'Schedule & Cross-Post',
        description:
          'Post now or schedule for later. Cross-post to X, Threads, and other platforms.',
      },
    ],
    useCases: [
      {
        title: 'Twitter/X Migrants',
        description:
          'Build your Bluesky presence while maintaining X. Post to both with one click.',
      },
      {
        title: 'Early Adopters',
        description:
          'Establish yourself on the decentralized web. Be where social media is heading.',
      },
      {
        title: 'Tech Enthusiasts',
        description:
          'Share thoughts on AT Protocol and decentralization. Reach the Bluesky community.',
      },
      {
        title: 'Multi-Platform Creators',
        description:
          'Add Bluesky to your social media mix without extra work. Cross-post everything.',
      },
    ],
    stats: {
      users: '25M+ users and growing',
      engagement: 'Engaged, tech-savvy community',
      growth: 'Early mover advantage',
    },
    faqs: [
      {
        question: 'Can I post to Bluesky instantly?',
        answer:
          'Yes! Post immediately or schedule for later. TheTabber gives you full flexibility for your Bluesky content.',
      },
      {
        question: 'Can I cross-post from X to Bluesky?',
        answer:
          'Absolutely! TheTabber lets you post the same content to both X and Bluesky (and 7 other platforms) with one click.',
      },
      {
        question: 'Does TheTabber support Bluesky images?',
        answer:
          'Yes! Post text and up to 4 images to Bluesky. You can also use our AI to help generate engaging captions.',
      },
      {
        question: "Is TheTabber's Bluesky scheduler free?",
        answer:
          'Yes! TheTabber offers a free tier for Bluesky scheduling. Premium plans unlock unlimited posts and AI features.',
      },
      {
        question: "What's an app password and why do I need one?",
        answer:
          'App passwords are secure tokens for third-party apps. Generate one in Bluesky Settings > App Passwords. This keeps your main password safe.',
      },
      {
        question: 'Can I manage multiple Bluesky accounts?',
        answer: 'Yes! Connect multiple Bluesky handles and post to all of them from one dashboard.',
      },
    ],
  },
  {
    slug: 'threads',
    name: 'Threads',
    displayName: 'Threads',
    color: '#000000',
    description:
      "Post and schedule to Threads with AI-generated content. Grow your audience on Meta's platform with easy multi-platform publishing.",
    longDescription:
      "TheTabber is the best Threads scheduler for creators who want to build presence on Meta's text-based platform. Schedule posts with text and images, let AI craft conversational content, and cross-post to Instagram, X, and 6 other platforms with one click. Connected through your Instagram account for seamless management. Perfect for creators who want to leverage their Instagram following on Threads without extra work.",
    metaDescription:
      'Free Threads scheduler - schedule posts & images. AI-powered captions. Post to Threads + Instagram + X in one click. Start free today.',
    keywords:
      'threads scheduler, schedule threads posts, threads post scheduler, meta threads scheduler, threads scheduling tool, threads automation, instagram threads scheduler, threads planner, social media scheduler',
    features: [
      'Post instantly or schedule posts',
      'AI-powered caption generation',
      'Connected with Instagram',
      'Multi-account support',
      'One-click cross-posting to 9+ platforms',
      'Image attachments',
      'Up to 500 characters per post',
      'Link sharing',
    ],
    howItWorks: [
      {
        step: 1,
        title: 'Connect via Instagram',
        description:
          'Threads uses your Instagram account for authentication. Link your Instagram and Threads is automatically available.',
      },
      {
        step: 2,
        title: 'Create Conversational Content',
        description:
          "Write authentic, engaging posts. AI helps craft content that fits Threads' conversational style.",
      },
      {
        step: 3,
        title: 'Schedule & Cross-Post',
        description:
          'Post now or schedule for later. Cross-post to Instagram, X, and other platforms.',
      },
    ],
    useCases: [
      {
        title: 'Instagram Creators',
        description:
          'Leverage your Instagram following on Threads. Cross-post content to both platforms.',
      },
      {
        title: 'X/Twitter Users',
        description: 'Build a Threads presence alongside X. Post to both with one click.',
      },
      {
        title: 'Personal Brands',
        description:
          'Share thoughts and build community on Threads. Let AI help craft your message.',
      },
      {
        title: 'Multi-Platform Creators',
        description:
          'Add Threads to your social media strategy without extra effort. Cross-post everything.',
      },
    ],
    stats: {
      users: '200M+ monthly users',
      engagement: 'Growing engaged community',
      growth: "Connected to Instagram's 2B users",
    },
    faqs: [
      {
        question: 'Can I post to Threads instantly?',
        answer:
          'Yes! Post immediately with one click or schedule for any future time. You control when your content goes live.',
      },
      {
        question: 'Can I post to both Instagram and Threads at once?',
        answer:
          "Absolutely! TheTabber's one-click publishing lets you post to Instagram, Threads, and 7 other platforms simultaneously.",
      },
      {
        question: "Does TheTabber's AI work with Threads?",
        answer:
          'Yes! Use our AI to generate engaging captions and optimize your content for the Threads audience.',
      },
      {
        question: "Is TheTabber's Threads scheduler free?",
        answer:
          'Yes! TheTabber offers a free tier for Threads scheduling. Premium plans unlock unlimited posts and AI features.',
      },
      {
        question: 'Do I need an Instagram account for Threads?',
        answer:
          "Yes, Threads is connected to Instagram. You'll authenticate through your Instagram account to access Threads scheduling.",
      },
      {
        question: "What's the character limit on Threads?",
        answer:
          'Threads posts can be up to 500 characters. TheTabber shows your character count as you write.',
      },
    ],
  },
];

/**
 * Get platform data by slug
 */
export function getPlatformBySlug(slug: string): PlatformData | undefined {
  return platformsData.find((p) => p.slug === slug);
}

/**
 * Get all platform slugs for sitemap generation
 */
export function getAllPlatformSlugs(): string[] {
  return platformsData.map((p) => p.slug);
}

/**
 * Tool-specific data for SEO pages
 */
export interface ToolData {
  slug: string;
  name: string;
  description: string;
  metaDescription: string;
  keywords: string;
  features: string[];
  faqs: Array<{ question: string; answer: string }>;
  category: 'image' | 'video' | 'audio';
  icon: string;
}

export const toolsData: ToolData[] = [
  {
    slug: 'image-optimizer',
    name: 'Free Image Optimizer',
    description:
      'Compress and optimize your images for web and social media. Reduce file sizes by up to 90% while maintaining quality. Your files are never stored. All processing happens locally in your browser.',
    metaDescription:
      'Free online image compressor and optimizer. Compress JPEG, PNG, WebP images instantly. Your files are never stored. All processing happens locally in your browser. Reduce image size by up to 90%.',
    keywords:
      'image compressor, image optimizer, compress images online, reduce image size, jpeg compressor, png optimizer, webp converter, free image compression, bulk image compressor',
    features: [
      'Compress images up to 90% smaller',
      'Supports JPEG, PNG, WebP, and more',
      'Batch processing - optimize multiple images at once',
      'Adjustable quality and max dimensions',
      '100% private - no images uploaded to servers',
      'Free to use with no limits',
    ],
    faqs: [
      {
        question: 'Is this image optimizer really free?',
        answer:
          'Yes! Our image optimizer is completely free to use with no limits. You can compress as many images as you want without creating an account.',
      },
      {
        question: 'Are my images uploaded to your servers?',
        answer:
          'No. All image processing happens directly in your browser using advanced compression algorithms. Your images never leave your device, ensuring complete privacy.',
      },
      {
        question: 'What image formats are supported?',
        answer:
          'Our optimizer supports JPEG, PNG, WebP, GIF, and most other common image formats. The compressed output maintains the original format for maximum compatibility.',
      },
      {
        question: 'How much can I reduce image file sizes?',
        answer:
          'Depending on the original image and your quality settings, you can typically reduce file sizes by 50-90% while maintaining excellent visual quality.',
      },
      {
        question: 'Can I optimize multiple images at once?',
        answer:
          'Yes! Simply drag and drop multiple images or select them from your file browser. All images will be processed and can be downloaded individually or all at once.',
      },
    ],
    category: 'image',
    icon: 'PhotoIcon',
  },
  {
    slug: 'video-optimizer',
    name: 'Free Video Optimizer',
    description:
      'Optimize and compress your videos for social media platforms. Reduce file sizes while maintaining quality. Your files are never stored - all processing happens locally in your browser using FFmpeg.',
    metaDescription:
      'Free online video optimizer and compressor. Optimize MP4, MOV, WebM videos for TikTok, Instagram, YouTube. No upload needed - process videos locally in your browser.',
    keywords:
      'video optimizer, video compressor, optimize video online, reduce video size, mp4 optimizer, video compression tool, social media video optimizer, tiktok video compressor, instagram video optimizer, free video optimizer',
    features: [
      'Reduce video file size by up to 90%',
      'Supports MP4, MOV, WebM, AVI, and more',
      'Adjustable quality and resolution settings',
      'Audio quality control options',
      '100% private - no videos uploaded to servers',
      'Powered by FFmpeg for professional results',
    ],
    faqs: [
      {
        question: 'Is this video optimizer really free?',
        answer:
          'Yes! Our video optimizer is completely free to use with no limits. You can optimize as many videos as you want without creating an account.',
      },
      {
        question: 'Are my videos uploaded to your servers?',
        answer:
          'No. All video processing happens directly in your browser using FFmpeg WebAssembly. Your videos never leave your device, ensuring complete privacy.',
      },
      {
        question: 'What video formats are supported?',
        answer:
          'Our optimizer supports MP4, MOV, WebM, AVI, MKV, and most common video formats. The optimized output is saved as MP4 for maximum compatibility.',
      },
      {
        question: 'How much can I reduce video file sizes?',
        answer:
          'Depending on the original video and your quality settings, you can typically reduce file sizes by 50-90% while maintaining good visual quality.',
      },
      {
        question: 'Can I optimize videos for social media?',
        answer:
          'Absolutely! Use the resolution presets (1080p, 720p, 480p) and quality settings to optimize your videos for TikTok, Instagram, YouTube, or any other platform.',
      },
    ],
    category: 'video',
    icon: 'VideoCameraIcon',
  },
  {
    slug: 'video-trimmer',
    name: 'Free Video Trimmer',
    description:
      'Trim and cut videos directly in your browser. No upload needed - all processing happens locally using FFmpeg. Perfect for creating clips for TikTok, Instagram Reels, and YouTube Shorts.',
    metaDescription:
      'Free online video trimmer. Cut and trim MP4, WebM, MOV videos instantly. Your files are never stored - all processing happens locally in your browser. Create clips for TikTok, Reels, and Shorts.',
    keywords:
      'video trimmer, video cutter, cut video online, trim video free, mp4 trimmer, video editor online, clip maker, tiktok video trimmer, instagram reels cutter, youtube shorts editor',
    features: [
      'Trim videos with precision timing',
      'Supports MP4, WebM, MOV, and more',
      'Visual timeline with draggable handles',
      'Preview before downloading',
      '100% private - no videos uploaded',
      'Powered by FFmpeg for professional results',
    ],
    faqs: [
      {
        question: 'Is this video trimmer really free?',
        answer:
          'Yes! Our video trimmer is completely free to use. You can trim as many videos as you want without creating an account or paying anything.',
      },
      {
        question: 'Are my videos uploaded to your servers?',
        answer:
          'No. All video processing happens directly in your browser. Your videos never leave your device, ensuring complete privacy and security.',
      },
      {
        question: 'What video formats are supported?',
        answer:
          'Our trimmer supports MP4, WebM, MOV, AVI, MKV, and most common video formats. The output is saved as MP4 for maximum compatibility.',
      },
      {
        question: 'Is there a file size limit?',
        answer:
          "Since processing happens in your browser, the limit depends on your device's memory. Most modern devices can handle videos up to 500MB-1GB without issues.",
      },
      {
        question: 'Can I use this to create TikTok or Instagram clips?',
        answer:
          'Absolutely! This trimmer is perfect for cutting clips for TikTok, Instagram Reels, YouTube Shorts, or any other platform. Just trim to your desired length and download.',
      },
    ],
    category: 'video',
    icon: 'ScissorsIcon',
  },
  {
    slug: 'mov-to-mp4',
    name: 'Free MOV to MP4 Converter',
    description:
      'Convert MOV videos to MP4 format instantly. Your files are never stored - all conversion happens locally in your browser. Perfect for sharing iPhone and Mac videos everywhere.',
    metaDescription:
      'Free online MOV to MP4 converter. Convert Apple QuickTime videos to MP4 instantly. No upload needed - convert videos locally in your browser. Works with iPhone and Mac videos.',
    keywords:
      'mov to mp4, convert mov to mp4, mov converter, quicktime to mp4, iphone video converter, mac video converter, free video converter, online video converter',
    features: [
      'Convert MOV to MP4 instantly',
      'Works with iPhone and Mac videos',
      'Multiple quality presets',
      'Fast conversion with FFmpeg',
      '100% private - no videos uploaded',
      'Free to use with no limits',
    ],
    faqs: [
      {
        question: 'Why convert MOV to MP4?',
        answer:
          'MP4 is the most universally compatible video format. While MOV works great on Apple devices, MP4 plays on virtually every device, browser, and social media platform.',
      },
      {
        question: 'Are my videos uploaded to a server?',
        answer:
          'No. All conversion happens directly in your browser. Your videos never leave your device, ensuring complete privacy.',
      },
      {
        question: 'Does conversion affect video quality?',
        answer:
          "Our converter offers multiple quality presets. The 'High Quality' preset maintains nearly identical quality to the original. You can also choose faster conversion with slightly reduced quality.",
      },
      {
        question: 'Can I convert iPhone videos?',
        answer:
          'Yes! This tool is perfect for converting iPhone videos (which are typically in MOV or HEVC format) to MP4 for sharing on social media or with non-Apple users.',
      },
      {
        question: 'Is there a file size limit?',
        answer:
          'Since processing happens in your browser, the limit depends on your device. Most modern computers can handle videos up to 1GB without issues.',
      },
    ],
    category: 'video',
    icon: 'ArrowPathIcon',
  },
  {
    slug: 'video-compressor',
    name: 'Free Video Compressor',
    description:
      'Reduce video file sizes without losing quality. Compress MP4, MOV, WebM videos directly in your browser. Perfect for sharing large videos on social media or via email.',
    metaDescription:
      'Free online video compressor. Reduce video file size by up to 90%. Your files are never stored - all processing happens locally in your browser.',
    keywords:
      'video compressor, compress video online, reduce video size, video size reducer, mp4 compressor, shrink video file, video compression tool, free video compressor',
    features: [
      'Reduce file size by up to 90%',
      'Adjustable quality settings',
      'Multiple resolution presets',
      'Audio bitrate control',
      '100% private - no videos uploaded',
      'Before/after size comparison',
    ],
    faqs: [
      {
        question: 'How much can I reduce my video size?',
        answer:
          'Depending on your settings and the original video, you can typically reduce file sizes by 50-90%. Higher compression means smaller files but slightly reduced quality.',
      },
      {
        question: 'Will compression affect video quality?',
        answer:
          "Our tool gives you full control over the quality/size tradeoff. You can choose from presets or fine-tune settings. The preview shows exactly what you'll get before downloading.",
      },
      {
        question: 'Are my videos uploaded anywhere?',
        answer:
          'No. All compression happens directly in your browser. Your videos never leave your device, ensuring complete privacy.',
      },
      {
        question: 'What video formats are supported?',
        answer:
          'Our compressor supports MP4, MOV, WebM, AVI, MKV, and most common video formats. The output is saved as MP4 for maximum compatibility.',
      },
      {
        question: 'Can I compress videos for email or social media?',
        answer:
          'Absolutely! Use our resolution presets (1080p, 720p, 480p) and quality settings to get your video under any size limit for email attachments or social media uploads.',
      },
    ],
    category: 'video',
    icon: 'ArrowsPointingInIcon',
  },
  {
    slug: 'video-to-gif',
    name: 'Free Video to GIF Converter',
    description:
      'Convert video clips to animated GIFs directly in your browser. Your files are never stored - all processing happens locally. Perfect for social media, messaging, and more.',
    metaDescription:
      'Free online video to GIF converter. Create animated GIFs from MP4, MOV, WebM videos. Trim clips, adjust FPS and size. No upload needed - convert locally in your browser.',
    keywords:
      'video to gif, convert video to gif, gif maker, mp4 to gif, mov to gif, animated gif creator, gif converter, free gif maker, online gif creator',
    features: [
      'Convert any video to GIF',
      'Trim clips to perfect length',
      'Adjustable FPS and dimensions',
      'Preview GIF before download',
      '100% private - no videos uploaded',
      'Optimized file sizes',
    ],
    faqs: [
      {
        question: 'Why create GIFs from videos?',
        answer:
          'GIFs are perfect for sharing short, looping clips on social media, in messages, or on websites. They play automatically and work everywhere without needing a video player.',
      },
      {
        question: 'Are my videos uploaded to a server?',
        answer:
          'No. All conversion happens directly in your browser. Your videos never leave your device, ensuring complete privacy.',
      },
      {
        question: 'How can I reduce GIF file size?',
        answer:
          'Use our settings to adjust width (smaller = smaller file), FPS (15-20 is usually good), and trim the clip to be as short as possible. These all significantly impact file size.',
      },
      {
        question: "What's the maximum GIF length?",
        answer:
          "While there's no hard limit, GIFs work best for short clips (3-10 seconds). Longer GIFs become very large files. For longer content, consider keeping it as a video.",
      },
      {
        question: 'Can I make GIFs from my phone videos?',
        answer:
          'Yes! Our tool works with videos from any device including iPhones and Android phones. Just upload your video, trim the section you want, and convert to GIF.',
      },
    ],
    category: 'video',
    icon: 'GifIcon',
  },
  {
    slug: 'audio-extractor',
    name: 'Free Audio Extractor',
    description:
      'Extract audio from any video file directly in your browser. Download as MP3, AAC, or WAV. Perfect for creating podcasts, music clips, or voice recordings from videos.',
    metaDescription:
      'Free online audio extractor. Extract audio from MP4, MOV, WebM videos. Download as MP3, AAC, or WAV. No upload needed - process videos locally in your browser.',
    keywords:
      'audio extractor, extract audio from video, video to mp3, video to audio, mp4 to mp3, mov to mp3, free audio extractor, online audio extractor, rip audio from video',
    features: [
      'Extract audio from any video',
      'Multiple output formats (MP3, AAC, WAV)',
      'Adjustable audio quality/bitrate',
      'Fast extraction with modern web technologies',
      '100% private - no videos uploaded',
      'Free to use with no limits',
    ],
    faqs: [
      {
        question: 'What audio formats can I extract to?',
        answer:
          'You can extract audio as MP3 (most compatible), AAC (better quality at same size), or WAV (uncompressed, highest quality). Choose based on your needs.',
      },
      {
        question: 'Are my videos uploaded to a server?',
        answer:
          'No. All extraction happens directly in your browser. Your videos never leave your device, ensuring complete privacy.',
      },
      {
        question: 'Will the audio quality be affected?',
        answer:
          'You can choose from multiple bitrate options. Higher bitrates preserve more quality. For most uses, 192kbps MP3 or 128kbps AAC provides excellent quality.',
      },
      {
        question: 'Can I extract audio from YouTube videos?',
        answer:
          "You'll need to first download the video file to your device, then use our tool to extract the audio. We don't support direct URL extraction.",
      },
      {
        question: 'What video formats are supported?',
        answer:
          'Our extractor supports MP4, MOV, WebM, AVI, MKV, and most common video formats. If your video plays in a browser, we can likely extract its audio.',
      },
    ],
    category: 'audio',
    icon: 'MusicalNoteIcon',
  },
];

/**
 * Get tool data by slug
 */
export function getToolBySlug(slug: string): ToolData | undefined {
  return toolsData.find((t) => t.slug === slug);
}

/**
 * Get all tool slugs for sitemap generation
 */
export function getAllToolSlugs(): string[] {
  return toolsData.map((t) => t.slug);
}

/**
 * Get all tools data
 */
export function getAllTools(): ToolData[] {
  return toolsData;
}

/**
 * Get tools grouped by category
 */
export function getToolsByCategory(): Record<string, ToolData[]> {
  return toolsData.reduce((acc, tool) => {
    if (!acc[tool.category]) {
      acc[tool.category] = [];
    }
    acc[tool.category].push(tool);
    return acc;
  }, {} as Record<string, ToolData[]>);
}
